import { MetricsDetails, PzResponse } from './model/pz-response';
import { BffResponseObject, ErrorResponse } from './model/response';
import { PzAnalytics, Program } from 'models';
import HttpMethod from 'sources/http-methods';
import { ClientPzMetricAnalyticsRequest, PzCampaigns, PzCurrencies, PzCurrenciesDetails } from 'src/models/pz';

export class PartnerPerfSource {
  private availablePzProgramsNames: Array<keyof typeof PzCampaigns>;
  private availablePzPrograms: Program[];
  private availableCurrencies: PzCurrenciesDetails[];

  constructor(programs: Program[], currencies: PzCurrenciesDetails[]) {
    const pzPrograms = programs.filter((program) => program.network === 'PARTNERIZE');
    this.availablePzPrograms = pzPrograms;
    this.availablePzProgramsNames = pzPrograms.map((program) => program.name as keyof typeof PzCampaigns);

    this.availableCurrencies = currencies.filter((currency) => currency.iso as keyof typeof PzCurrencies);
  }

  public async getPzAnalytics(
    clientRequest: ClientPzMetricAnalyticsRequest,
  ): Promise<BffResponseObject<MetricsDetails[]>> {
    const pzMetricRequest: PzAnalytics = {
      ...clientRequest,
      programs: this.availablePzProgramsNames,
    };

    const response = await fetch('/api/v1/pz/analytics', {
      method: HttpMethod.POST,
      body: JSON.stringify(pzMetricRequest),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      } as HeadersInit,
    });

    const data: PzResponse = await response.json();

    // This merges the clicks and the conversions (which are two separate arrays) into one:
    // [ {
    //    campaign_name: our internal name,
    //    total_clicks: from clicks
    //    total_order_value: from conversions
    //    total_partner_commission: from conversions
    //    total_conversions: from conversions
    // } ]
    if (data.success === true) {
      const overallMetrics = data.body.clicks.map((clickMetric) => {
        const targetedConversionMetric = data.body.conversions.find(
          (conversion) => conversion.campaign_id === clickMetric.campaign_id,
        );

        return {
          ...clickMetric,
          ...targetedConversionMetric,
        };
      });

      return {
        success: true,
        error: null,
        resBody: overallMetrics,
      };
    } else {
      return Promise.reject({
        message: data.errorCode,
        detail: {
          statusCode: data.body.code,
          statusMessage: data.body.message,
        },
      } as ErrorResponse<{ statusCode: string; statusMessage: string }>);
    }
  }

  public getAvailablePzPrograms(): Program[] {
    return this.availablePzPrograms;
  }

  public getAvaliablePzCurrencies(): PzCurrenciesDetails[] {
    return this.availableCurrencies;
  }
}
